import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import DynamicMetaTags from '../components/ui/DynamicMetaTags';
import { usePublicTheme } from '../hooks/usePublicTheme';
import '../styles/publicTheme.css';
import LandingNavbar from '../components/landing/LandingNavbar';
import LandingHero from '../components/landing/LandingHero';
import LandingStats from '../components/landing/LandingStats';
import LandingFeatures from '../components/landing/LandingFeatures';
import LandingHowItWorks from '../components/landing/LandingHowItWorks';
import LandingTestimonials from '../components/landing/LandingTestimonials';
import LandingCoachesBanner from '../components/landing/LandingCoachesBanner';
import LandingPartners from '../components/landing/LandingPartners';
import LandingPricing from '../components/landing/LandingPricing';
import LandingCTA from '../components/landing/LandingCTA';
import LandingFooter from '../components/landing/LandingFooter';
import LandingAudiences from '../components/landing/LandingAudiences';
import LandingShowcase from '../components/landing/LandingShowcase';
import LandingMigration from '../components/landing/LandingMigration';
import LandingFAQ from '../components/landing/LandingFAQ';
import MobileCTABar from '../components/landing/MobileCTABar';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL;

const DEFAULT_SECTIONS = [
  { key: 'navbar', visible: true, order: 1 },
  { key: 'hero', visible: true, order: 2 },
  { key: 'stats', visible: true, order: 3 },
  { key: 'audiences', visible: true, order: 3.5 },
  { key: 'features', visible: true, order: 4 },
  { key: 'showcase', visible: true, order: 4.5 },
  { key: 'migration', visible: true, order: 5.5 },
  { key: 'faq', visible: true, order: 6.5 },
  { key: 'how_it_works', visible: true, order: 5 },
  { key: 'testimonials', visible: true, order: 6 },
  { key: 'cta', visible: true, order: 7 },
  { key: 'coaches_banner', visible: true, order: 8 },
  { key: 'footer', visible: true, order: 9 },
  { key: 'partners', visible: false, order: 10 },
  { key: 'pricing', visible: false, order: 11 },
];

const injectFonts = () => {
  if (document.getElementById('landing-fonts-link')) return;
  const link = document.createElement('link');
  link.id = 'landing-fonts-link';
  link.rel = 'stylesheet';
  link.href = 'https://fonts.googleapis.com/css2?family=Cairo:wght@700;800;900&family=Tajawal:wght@400;500;700&display=swap';
  document.head.appendChild(link);

  if (document.getElementById('tasyar-splash-style')) return;
  const style = document.createElement('style');
  style.id = 'tasyar-splash-style';
  style.textContent = `
    @keyframes tasyarSplashIn { 0% { opacity: 0; transform: scale(0.85); } 60% { opacity: 1; transform: scale(1.04); } 100% { opacity: 1; transform: scale(1); } }
    .animate-tasyar-splash { animation: tasyarSplashIn 0.6s cubic-bezier(0.22, 1, 0.36, 1) both; }
  `;
  document.head.appendChild(style);
};

const LandingPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { theme, toggleTheme } = usePublicTheme();
  const isPreview = useMemo(() => new URLSearchParams(location.search).get('landing_preview') === '1', [location.search]);
  const [scrolled, setScrolled] = useState(false);
  const [platformSettings, setPlatformSettings] = useState({ platform_name: 'تسيار', platform_logo: null, platform_logo_full: null });
  const [landing, setLanding] = useState(null);
  const [partners, setPartners] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [plans, setPlans] = useState(null);
  const [stats, setStats] = useState(null);
  const [settingsReady, setSettingsReady] = useState(false);

  // تحميل مسبق لكل من الشعارين الليلي والنهاري لحظة توفر الإعدادات، لتبديل فوري بلا تأخير عند تغيير الوضع
  useEffect(() => {
    if (!settingsReady) return;
    const ids = [platformSettings.platform_logo_full_dark, platformSettings.platform_logo_full_light, platformSettings.platform_logo_full].filter(Boolean);
    [...new Set(ids)].forEach((id) => {
      const img = new window.Image();
      img.src = `${API_BASE_URL}/api/files/${id}`;
    });
  }, [settingsReady, platformSettings.platform_logo_full_dark, platformSettings.platform_logo_full_light, platformSettings.platform_logo_full]);

  useEffect(() => {
    injectFonts();

    const load = async () => {
      const safeGet = async (url) => {
        try {
          const res = await fetch(`${API_BASE_URL}${url}`);
          return res.ok ? await res.json() : null;
        } catch {
          return null;
        }
      };

      const settingsPromise = safeGet('/api/platform/settings').then((settingsData) => {
        if (settingsData) setPlatformSettings(settingsData);
        setSettingsReady(true);
        return settingsData;
      });

      const [landingData, , partnersData, testimonialsData, plansData, statsData] = await Promise.all([
        safeGet('/api/saas/landing-page'),
        settingsPromise,
        safeGet('/api/partners'),
        safeGet('/api/testimonials'),
        safeGet('/api/saas/plans'),
        safeGet('/api/saas/public-stats'),
      ]);

      if (landingData) {
        setLanding(landingData);
        if (!isPreview && landingData.page_title) document.title = landingData.page_title;
      }
      if (Array.isArray(partnersData)) {
        setPartners(partnersData.filter((p) => p.is_active !== false).sort((a, b) => (a.order_index || 0) - (b.order_index || 0)));
      }
      if (Array.isArray(testimonialsData)) setTestimonials(testimonialsData);
      if (Array.isArray(plansData)) setPlans(plansData);
      if (statsData) setStats(statsData);
    };
    load();

    const handleScroll = () => setScrolled(window.scrollY > 10);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [isPreview]);

  // معاينة مباشرة: يستقبل محتوى المسودة فور الكتابة في المحرر (Super Admin) عبر postMessage
  useEffect(() => {
    if (!isPreview) return;
    const handler = (e) => {
      if (e.data?.type === 'TASYAR_LANDING_PREVIEW') setLanding(e.data.payload);
    };
    window.addEventListener('message', handler);
    try { window.parent?.postMessage({ type: 'TASYAR_LANDING_PREVIEW_READY' }, window.location.origin); } catch { /* noop */ }
    return () => window.removeEventListener('message', handler);
  }, [isPreview]);

  const orderedSections = useMemo(() => {
    const saved = Array.isArray(landing?.sections) && landing.sections.length ? landing.sections : [];
    const list = saved.length
      ? [...saved, ...DEFAULT_SECTIONS.filter((d) => !saved.some((s) => s.key === d.key))]
      : DEFAULT_SECTIONS;
    const middle = list.filter((s) => s.key !== 'navbar' && s.key !== 'footer').sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
    const navbar = list.find((s) => s.key === 'navbar') || DEFAULT_SECTIONS[0];
    const footer = list.find((s) => s.key === 'footer') || DEFAULT_SECTIONS[DEFAULT_SECTIONS.length - 1];
    // الشريط العلوي يبقى أولاً والتذييل يبقى أخيراً دائماً بصرف النظر عن الترتيب المحفوظ
    return [navbar, ...middle, footer];
  }, [landing]);

  const isVisible = (key) => {
    const found = orderedSections.find((s) => s.key === key);
    return found ? found.visible !== false : true;
  };

  const SECTION_COMPONENTS = {
    navbar: () => <LandingNavbar platformSettings={platformSettings} scrolled={scrolled} navigate={navigate} theme={theme} onToggleTheme={toggleTheme} />,
    hero: () => <LandingHero landing={landing} navigate={navigate} />,
    stats: () => <LandingStats landing={landing} stats={stats} />,
    audiences: () => <LandingAudiences landing={landing} navigate={navigate} />,
    features: () => <LandingFeatures landing={landing} />,
    showcase: () => <LandingShowcase landing={landing} />,
    migration: () => <LandingMigration landing={landing} navigate={navigate} />,
    faq: () => <LandingFAQ landing={landing} />,
    how_it_works: () => <LandingHowItWorks landing={landing} />,
    testimonials: () => <LandingTestimonials landing={landing} testimonials={testimonials} />,
    coaches_banner: () => <LandingCoachesBanner landing={landing} navigate={navigate} />,
    partners: () => <LandingPartners landing={landing} partners={partners} />,
    pricing: () => <LandingPricing landing={landing} plans={plans} navigate={navigate} />,
    cta: () => <LandingCTA landing={landing} navigate={navigate} />,
    footer: () => <LandingFooter platformSettings={platformSettings} landing={landing} theme={theme} />,
  };

  return (
    <div className="landing-root min-h-screen bg-[#060f1c] pb-16 text-white lg:pb-0" dir="rtl" data-testid="landing-page" data-pub-theme={theme}>
      <DynamicMetaTags platformSettings={platformSettings} landing={landing} />
      {orderedSections
        .filter((s) => isVisible(s.key) && SECTION_COMPONENTS[s.key])
        .map((s) => <React.Fragment key={s.key}>{SECTION_COMPONENTS[s.key]()}</React.Fragment>)}
      <MobileCTABar navigate={navigate} />
    </div>
  );
};

export default LandingPage;

